import React from "react";

import { cn } from "@/lib/utils";

import { type VariantProps, cva } from "class-variance-authority";

const containerVariants = cva("flex", {
  variants: {
    height: {
      full: "h-full",
      screen: "h-screen",
      "1/4": "h-1/4",
      "1/3": "h-1/3",
      "1/2": "h-1/2",
      "2/3": "h-2/3",
      "3/4": "h-3/4",
    },
    width: {
      full: "w-full",
      screen: "w-screen",
      "1/4": "w-1/4",
      "1/3": "w-1/3",
      "1/2": "w-1/2",
      "2/3": "w-2/3",
      "3/4": "w-3/4",
    },
    padding: {
      0: "p-0",
      2: "p-0.5",
      4: "p-1",
      8: "p-2",
      12: "p-3",
      16: "p-4",
      20: "p-5",
    },
    margin: {
      auto: "m-auto",
      0: "m-0",
      2: "m-0.5",
      4: "m-1",
      8: "m-2",
      12: "m-3",
      16: "m-4",
      20: "m-5",
    },
    direction: {
      row: "flex-row",
      column: "flex-col",
    },
    overflow: {
      hidden: "overflow-hidden",
      auto: "overflow-auto",
      scroll: "overflow-scroll",
      visible: "overflow-visible",
    },
  },
  defaultVariants: {
    height: "full",
    width: "full",
    padding: 0,
    margin: 0,
    direction: "row",
    overflow: "auto",
  },
});

export type ContainerProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof containerVariants> & {
    className?: string;
  };

const Container = React.forwardRef<HTMLDivElement, ContainerProps>(
  (
    {
      className,
      overflow = "scroll",
      direction = "row",
      height = "full",
      width = "full",
      padding = 0,
      margin = 0,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        containerVariants({
          height,
          overflow,
          direction,
          width,
          padding,
          margin,
          className,
        }),
      )}
      {...props}
    />
  ),
);
Container.displayName = "Container";

export { Container, containerVariants };
